import moment from 'moment';
import { DynamicObjectInterface } from '../../sdk/type';
import { getRoundedNumber } from './common';
import { decryptString } from './cryptoUtils';

interface SubKpi {
  indicatorName: string;
  indicatorMeasurementUnit: string;
  subKpiCode: string;
}

const generateDynamicExcelHeaders = (subKpiCode: SubKpi[]): string[] => {
  const preIndicatorHeaders = [
    'Team Name',
    'Team ID',
    'Team Description',
    'Team Start Date DD-MM-YYYY',
    'Team End Date DD-MM-YYYY',
    'Team Status',
    'Team Size',
    'Participation Type',
    'Name',
    'ABO ID',
    'Party ID',
    'is Team Leader',
    'is New ABO',
    'Contact',
    'Email',
    'Address',
    'Gender',
    'Account Type',
    'Current Pin Level',
    'Highest Pin Achieved',
    'BA/LOA Name',
    'Remark',
    'Status',
    'Order Number',
    'Product Name',
    'Order Qualified',
  ];

  const dynamicIndicators = subKpiCode.map(
    (kpi: DynamicObjectInterface) => `${kpi.indicatorName} (${kpi.indicatorMeasurementUnit})`,
  );

  const postDiffDynamicIndicators = subKpiCode.map(
    (kpi: DynamicObjectInterface) => `${kpi.indicatorName} diff (${kpi.indicatorMeasurementUnit})`,
  );

  const preAssessmentsHeaders = ['Date', ...dynamicIndicators, 'Photo - Front', 'Photo - Back'];

  const postAssessmentsHeaders = ['Date', ...dynamicIndicators, 'Photo - Front', 'Photo - Back'];

  const postIndicatorHeaders = [...postDiffDynamicIndicators, 'Achieved Individual Criteria', 'Achieved Team Criteria'];

  return [...preIndicatorHeaders, ...preAssessmentsHeaders, ...postAssessmentsHeaders, ...postIndicatorHeaders];
};

const generateDynamicExcelColumnKeys = (subKpiCode: SubKpi[]): { key: string }[] => {
  const preIndicatorKeys = [
    { key: 'teamName' },
    { key: 'teamId' },
    { key: 'teamDescription' },
    { key: 'teamStartDate' },
    { key: 'teamEndDate' },
    { key: 'teamStatus' },
    { key: 'teamSize' },
    { key: 'participationType' },
    { key: 'name' },
    { key: 'aboId' },
    { key: 'partyId' },
    { key: 'isTeamLeader' },
    { key: 'isNewAbo' },
    { key: 'phone' },
    { key: 'email' },
    { key: 'address' },
    { key: 'gender' },
    { key: 'accountType' },
    { key: 'currentPinLevel' },
    { key: 'highestPinAchieved' },
    { key: 'baLoaName' },
    { key: 'remark' },
    { key: 'enrollmentStatus' },
    { key: 'orderNumber' },
    { key: 'productName' },
    { key: 'orderQualified' },
  ];

  const preAssessmentDynamicIndicators = subKpiCode.map((kpi: DynamicObjectInterface) => {
    return { key: generateSensitiveKeyFromIndicator(kpi.subKpiCode, 'weightIn') };
  });

  const postAssessmentDynamicIndicators = subKpiCode.map((kpi: DynamicObjectInterface) => {
    return { key: generateSensitiveKeyFromIndicator(kpi.subKpiCode, 'weightOut') };
  });

  const postDiffDynamicIndicators = subKpiCode.map((kpi: DynamicObjectInterface) => {
    return { key: generateSensitiveKeyFromIndicator(kpi.subKpiCode, 'participant', 'Diff') };
  });

  const preAssessmentsHeaders = [
    { key: 'weightInDate' },
    ...preAssessmentDynamicIndicators,
    { key: 'weightInPhotoFront' },
    { key: 'weightInPhotoBack' },
  ];

  const postAssessmentsHeaders = [
    { key: 'weightOutDate' },
    ...postAssessmentDynamicIndicators,
    { key: 'weightOutPhotoFront' },
    { key: 'weightOutPhotoBack' },
  ];

  const postIndicatorHeaders = [
    ...postDiffDynamicIndicators,
    { key: 'achievedIndividualCriteria' },
    { key: 'achievedGroupCriteria' },
  ];

  return [...preIndicatorKeys, ...preAssessmentsHeaders, ...postAssessmentsHeaders, ...postIndicatorHeaders];
};

const generateCamelCaseKeyFromIndicator = (indicatorName: string, prefix: string = '', suffix: string = ''): string => {
  const camel = indicatorName
    .replace(/[^a-zA-Z0-9 ]/g, '') // remove non-alphanumeric except spaces
    .trim()
    .split(/\s+/) // split by one or more spaces
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('');

  const base = camel.charAt(0).toUpperCase() + camel.slice(1); // Capitalize first for combining

  return `${prefix}${base}${suffix}`;
};

const generateSensitiveKeyFromIndicator = (indicatorName: string, prefix: string = '', suffix: string = ''): string => {
  const cleaned = indicatorName.replace(/[^a-zA-Z0-9 \-_.()]/g, '').trim(); // allow letters, numbers, space, dash, underscore
  return `${prefix}${cleaned}${suffix}`;
};

const getValueForKey = (key: string, item: DynamicObjectInterface): DynamicObjectInterface => {
  // Handle known static keys directly
  switch (key) {
    case 'teamName':
      return item.teamName;
    case 'teamId':
      return item.teamId;
    case 'teamDescription':
      return item.team?.teamDescription;
    case 'teamStartDate':
      return item.team?.teamStartDate ? moment(item.team?.teamStartDate).format('DD-MM-YYYY') : '';
    case 'teamEndDate':
      return item.team?.teamEndDate ? moment(item.team?.teamEndDate).format('DD-MM-YYYY') : '';
    case 'teamStatus':
      return item.team?.teamStatus;
    case 'teamSize':
      return item.team?.participationType === 'ind' ? 'N/A' : item.team?.teamSize;
    case 'participationType':
      return item.team?.participationType === 'ind' ? 'Individual' : 'Team';
    case 'name':
      return item.name;
    case 'aboId':
      return item.loginId;
    case 'partyId':
      return item.partyId;
    case 'isTeamLeader':
      return item.isTeamLeader ? 'Y' : 'N';
    case 'isNewAbo':
      return item.isNewAbo === 1 ? 'Y' : 'N';
    case 'phone':
      return item.phone;
    case 'email':
      return item.email;
    case 'address':
      return item.address;
    case 'gender':
      return item.gender;
    case 'accountType':
      return item.accounType;
    case 'currentPinLevel':
      return item.pinLevel;
    case 'highestPinAchieved':
      return item.highestPinAchieved;
    case 'baLoaName':
      return item.baLoaName;
    case 'remark':
      return item.remark;
    case 'enrollmentStatus':
      return item.enrollmentStatus;
    case 'orderNumber':
      return item?.purchase?.[0]?.orderNumber;
    case 'productName':
      return item?.purchase?.[0]?.orderName;
    case 'orderQualified':
      return item?.purchase?.[0]?.orderStatus === 1 ? 'Y' : 'N';
    case 'weightInDate':
      return item.weightIn?.weightedDate ? moment(item.weightIn.weightedDate).format('DD-MM-YYYY') : '';
    case 'weightOutDate':
      return item.weightOut?.weightedDate ? moment(item.weightOut.weightedDate).format('DD-MM-YYYY') : '';
    case 'weightInPhotoFront':
      return item.weightIn?.weightInPhotoFront ? 'Y' : 'N';
    case 'weightInPhotoBack':
      return item.weightIn?.weightInPhotoBack ? 'Y' : 'N';
    case 'weightOutPhotoFront':
      return item.weightOut?.weightOutPhotoFront ? 'Y' : 'N';
    case 'weightOutPhotoBack':
      return item.weightOut?.weightOutPhotoBack ? 'Y' : 'N';
    case 'achievedIndividualCriteria':
      return item.isIndividualAchieved === 1 ? 'Y' : 'N';
    case 'achievedGroupCriteria':
      return item.isGroupAchieved === 1 ? 'Y' : 'N';
  }

  if (key.startsWith('weightIn')) {
    const subKey = key.replace('weightIn', '');
    return item.weightIn?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightIn.otherIndicators[subKey]))
      : '';
  }

  if (key.startsWith('weightOut')) {
    const subKey = key.replace('weightOut', '');
    return item.weightOut?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightOut.otherIndicators[subKey]))
      : '';
  }

  if (key.startsWith('participant') && key.endsWith('Diff')) {
    const subKey = key.replace('participant', '').replace('Diff', '');
    const weightInValue = item.weightIn?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightIn.otherIndicators[subKey]))
      : 0;
    const weightOutValue = item.weightOut?.otherIndicators?.[subKey]
      ? getRoundedNumber(decryptString(item.weightOut.otherIndicators[subKey]))
      : 0;

    const weightOutDiffValues =
      weightInValue !== null && weightOutValue !== null ? getRoundedNumber(weightOutValue - weightInValue) : 0;

    return weightOutDiffValues.toFixed(1);
  }

  return '';
};

// Convert column index (e.g. 1, 27) to Excel letter (A, AA, etc.)
const getExcelAlpha = (col: number): string => {
  let alpha = '';
  while (col > 0) {
    const rem = (col - 1) % 26;
    alpha = String.fromCharCode(65 + rem) + alpha;
    col = Math.floor((col - 1) / 26);
  }
  return alpha;
};

export {
  generateDynamicExcelHeaders,
  generateDynamicExcelColumnKeys,
  generateCamelCaseKeyFromIndicator,
  generateSensitiveKeyFromIndicator,
  getValueForKey,
  getExcelAlpha,
};
